import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';

interface AccessLog {
  id: string;
  user_id: string;
  user_role: string;
  patient_id: string | null;
  action_type: string;
  resource_type: string;
  resource_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  details: any;
  created_at: string;
  user_email?: string;
  user_name?: string;
  patient_name?: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(str: string | null | undefined): str is string {
  return typeof str === 'string' && UUID_REGEX.test(str);
}

export default function AccesLogsPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    actionType: '',
    resourceType: '',
    dateFrom: '',
    dateTo: '',
    searchUser: '',
  });

  useEffect(() => {
    if (!authLoading && (!user || role !== 'super_admin')) {
      navigate('/login', { replace: true });
    }
  }, [user, role, authLoading, navigate]);

  useEffect(() => {
    if (user && role === 'super_admin') {
      loadLogs();
    }
  }, [user, role, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('access_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.actionType) {
        query = query.eq('action_type', filters.actionType);
      }

      if (filters.resourceType) {
        query = query.eq('resource_type', filters.resourceType);
      }

      if (filters.dateFrom) {
        query = query.gte('created_at', new Date(filters.dateFrom).toISOString());
      }

      if (filters.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      // Enrichir avec les informations utilisateur et patient
      const enrichedLogs = await Promise.all(
        (data || []).map(async (log) => {
          const enriched: AccessLog = { ...log };

          // Récupérer l'email de l'utilisateur (seulement si user_id est un UUID valide)
          if (isValidUUID(log.user_id)) {
            try {
              const { data: userData } = await supabase.auth.admin.getUserById(log.user_id);
              if (userData?.user?.email) {
                enriched.user_email = userData.user.email;
              }
            } catch {
              // Silencieux : on continue même si la récupération échoue
            }
          }

          // Récupérer le nom de l'utilisateur selon son rôle
          if (isValidUUID(log.user_id)) {
            if (log.user_role === 'medecin') {
              const { data: medecinData } = await supabase
                .from('medecins')
                .select('nom, prenom')
                .eq('user_id', log.user_id)
                .single();

              if (medecinData) {
                enriched.user_name = `Dr ${medecinData.nom} ${medecinData.prenom}`;
              }
            } else if (log.user_role === 'patient') {
              const { data: patientData } = await supabase
                .from('patients')
                .select('nom, prenom')
                .eq('user_id', log.user_id)
                .single();

              if (patientData) {
                enriched.user_name = `${patientData.nom} ${patientData.prenom}`;
              }
            } else if (log.user_role === 'secretaire') {
              const { data: secretaireData } = await supabase
                .from('secretaires')
                .select('nom, prenom')
                .eq('user_id', log.user_id)
                .single();

              if (secretaireData) {
                enriched.user_name = `${secretaireData.nom} ${secretaireData.prenom}`;
              }
            } else if (log.user_role === 'super_admin') {
              const { data: adminData } = await supabase
                .from('super_admins')
                .select('nom, prenom')
                .eq('user_id', log.user_id)
                .single();

              if (adminData) {
                enriched.user_name = `${adminData.nom} ${adminData.prenom}`;
              }
            }
          }

          // Récupérer le nom du patient si applicable
          if (log.patient_id) {
            const { data: patientData } = await supabase
              .from('patients')
              .select('nom, prenom')
              .eq('id', log.patient_id)
              .single();

            if (patientData) {
              enriched.patient_name = `${patientData.nom} ${patientData.prenom}`;
            }
          }

          return enriched;
        })
      );

      // Filtrer par recherche utilisateur si nécessaire
      let filteredLogs = enrichedLogs;
      if (filters.searchUser) {
        const searchLower = filters.searchUser.toLowerCase();
        filteredLogs = enrichedLogs.filter(
          (log) =>
            log.user_email?.toLowerCase().includes(searchLower) ||
            log.user_name?.toLowerCase().includes(searchLower) ||
            log.patient_name?.toLowerCase().includes(searchLower)
        );
      }

      setLogs(filteredLogs);
    } catch (error) {
      console.error('Erreur lors du chargement des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'VIEW':
        return 'ri-eye-line';
      case 'CREATE':
        return 'ri-add-circle-line';
      case 'UPDATE':
        return 'ri-edit-line';
      case 'DELETE':
        return 'ri-delete-bin-line';
      case 'DOWNLOAD':
        return 'ri-download-line';
      case 'SHARE':
        return 'ri-share-line';
      case 'PROMOTE':
        return 'ri-arrow-up-circle-line';
      case 'REVOKE':
        return 'ri-arrow-down-circle-line';
      case 'INVITE':
        return 'ri-mail-send-line';
      case 'REGISTER':
        return 'ri-user-add-line';
      case 'SUSPEND':
        return 'ri-user-unfollow-line';
      case 'REACTIVATE':
        return 'ri-user-follow-line';
      case 'LOGIN':
        return 'ri-login-box-line';
      case 'LOGOUT':
        return 'ri-logout-box-line';
      case 'LOGIN_FAILED':
        return 'ri-spam-line';
      case 'PASSWORD_CHANGE':
        return 'ri-lock-password-line';
      case 'PASSWORD_RESET':
        return 'ri-lock-unlock-line';
      case 'CONFIG_UPDATE':
        return 'ri-settings-3-line';
      case 'MAINTENANCE_TOGGLE':
        return 'ri-tools-line';
      case 'SEND_SMS':
        return 'ri-chat-1-line';
      case 'SEND_EMAIL':
        return 'ri-mail-line';
      case 'SEND_NOTIFICATION':
        return 'ri-notification-3-line';
      default:
        return 'ri-file-list-line';
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'VIEW':
        return 'text-blue-600 bg-blue-50';
      case 'CREATE':
        return 'text-green-600 bg-green-50';
      case 'UPDATE':
        return 'text-amber-600 bg-amber-50';
      case 'DELETE':
        return 'text-red-600 bg-red-50';
      case 'DOWNLOAD':
        return 'text-purple-600 bg-purple-50';
      case 'SHARE':
        return 'text-orange-600 bg-orange-50';
      case 'PROMOTE':
        return 'text-emerald-600 bg-emerald-50';
      case 'REVOKE':
        return 'text-yellow-600 bg-yellow-50';
      case 'INVITE':
        return 'text-cyan-600 bg-cyan-50';
      case 'REGISTER':
        return 'text-teal-600 bg-teal-50';
      case 'SUSPEND':
        return 'text-rose-600 bg-rose-50';
      case 'REACTIVATE':
        return 'text-lime-600 bg-lime-50';
      case 'LOGIN':
        return 'text-indigo-600 bg-indigo-50';
      case 'LOGOUT':
        return 'text-gray-600 bg-gray-100';
      case 'LOGIN_FAILED':
        return 'text-red-700 bg-red-100';
      case 'PASSWORD_CHANGE':
      case 'PASSWORD_RESET':
        return 'text-violet-600 bg-violet-50';
      case 'CONFIG_UPDATE':
        return 'text-slate-600 bg-slate-100';
      case 'MAINTENANCE_TOGGLE':
        return 'text-stone-600 bg-stone-100';
      case 'SEND_SMS':
      case 'SEND_EMAIL':
      case 'SEND_NOTIFICATION':
        return 'text-sky-600 bg-sky-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getActionLabel = (actionType: string) => {
    switch (actionType) {
      case 'VIEW': return 'Consultation';
      case 'CREATE': return 'Création';
      case 'UPDATE': return 'Modification';
      case 'DELETE': return 'Suppression';
      case 'DOWNLOAD': return 'Téléchargement';
      case 'SHARE': return 'Partage';
      case 'PROMOTE': return 'Promotion';
      case 'REVOKE': return 'Révocation';
      case 'INVITE': return 'Invitation';
      case 'REGISTER': return 'Inscription';
      case 'SUSPEND': return 'Suspension';
      case 'REACTIVATE': return 'Réactivation';
      case 'LOGIN': return 'Connexion';
      case 'LOGOUT': return 'Déconnexion';
      case 'LOGIN_FAILED': return 'Échec connexion';
      case 'PASSWORD_CHANGE': return 'Chgt mot de passe';
      case 'PASSWORD_RESET': return 'Reset mot de passe';
      case 'CONFIG_UPDATE': return 'Config système';
      case 'MAINTENANCE_TOGGLE': return 'Maintenance';
      case 'SEND_SMS': return 'Envoi SMS';
      case 'SEND_EMAIL': return 'Envoi Email';
      case 'SEND_NOTIFICATION': return 'Notification';
      default: return actionType;
    }
  };

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'DOSSIER_MEDICAL': return 'ri-folder-user-line';
      case 'PRESCRIPTION': return 'ri-file-list-3-line';
      case 'RESULTAT_TEST': return 'ri-flask-line';
      case 'RENDEZ_VOUS': return 'ri-calendar-event-line';
      case 'SUIVI': return 'ri-pulse-line';
      case 'ADMINISTRATEUR': return 'ri-shield-user-line';
      case 'MEDECIN': return 'ri-stethoscope-line';
      case 'SECRETAIRE': return 'ri-user-settings-line';
      case 'PATIENT': return 'ri-user-heart-line';
      case 'SYSTEM_CONFIG': return 'ri-settings-3-line';
      case 'NOTIFICATION': return 'ri-notification-3-line';
      case 'ACTUALITE': return 'ri-newspaper-line';
      case 'SESSION': return 'ri-key-2-line';
      case 'MESSAGE_CONTACT': return 'ri-message-2-line';
      case 'SMS': return 'ri-chat-1-line';
      case 'EMAIL': return 'ri-mail-line';
      case 'CONSULTATION': return 'ri-chat-voice-line';
      case 'PARTAGE_DOSSIER': return 'ri-share-forward-line';
      case 'DISPONIBILITE': return 'ri-time-line';
      default: return 'ri-file-line';
    }
  };

  const getResourceLabel = (resourceType: string) => {
    switch (resourceType) {
      case 'DOSSIER_MEDICAL': return 'Dossier médical';
      case 'PRESCRIPTION': return 'Prescription';
      case 'RESULTAT_TEST': return 'Résultat test';
      case 'RENDEZ_VOUS': return 'Rendez-vous';
      case 'SUIVI': return 'Suivi patient';
      case 'ADMINISTRATEUR': return 'Administrateur';
      case 'MEDECIN': return 'Médecin';
      case 'SECRETAIRE': return 'Secrétaire';
      case 'PATIENT': return 'Patient';
      case 'SYSTEM_CONFIG': return 'Config. système';
      case 'NOTIFICATION': return 'Notification';
      case 'ACTUALITE': return 'Actualité';
      case 'SESSION': return 'Session';
      case 'MESSAGE_CONTACT': return 'Message contact';
      case 'SMS': return 'SMS';
      case 'EMAIL': return 'Email';
      case 'CONSULTATION': return 'Consultation';
      case 'PARTAGE_DOSSIER': return 'Partage dossier';
      case 'DISPONIBILITE': return 'Disponibilité';
      default: return resourceType;
    }
  };

  const exportLogs = () => {
    const csv = [
      ['Date', 'Utilisateur', 'Rôle', 'Action', 'Ressource', 'Patient', 'IP', 'Détails'].join(','),
      ...logs.map((log) =>
        [
          new Date(log.created_at).toLocaleString('fr-FR'),
          log.user_name || log.user_email || log.user_id,
          log.user_role,
          log.action_type,
          log.resource_type,
          log.patient_name || '-',
          log.ip_address || '-',
          JSON.stringify(log.details || {}),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-clinic-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="text-clinic-blue hover:text-clinic-blue-dark mb-4 flex items-center"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Retour au tableau de bord
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-clinic-blue-dark">Historique des Accès</h1>
              <p className="text-gray-600 mt-2">
                Journalisation conforme à la loi n°2017-20 du Bénin (APDP)
              </p>
            </div>
            <button
              onClick={exportLogs}
              className="px-6 py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg flex items-center whitespace-nowrap"
            >
              <i className="ri-download-line mr-2"></i>
              Exporter CSV
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-clinic-blue-dark mb-4">Filtres</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
              <select
                value={filters.actionType}
                onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              >
                <option value="">Toutes</option>
                <option value="VIEW">Consultation</option>
                <option value="CREATE">Création</option>
                <option value="UPDATE">Modification</option>
                <option value="DELETE">Suppression</option>
                <option value="DOWNLOAD">Téléchargement</option>
                <option value="SHARE">Partage</option>
                <option value="PROMOTE">Promotion</option>
                <option value="REVOKE">Révocation</option>
                <option value="INVITE">Invitation</option>
                <option value="REGISTER">Inscription</option>
                <option value="LOGIN">Connexion</option>
                <option value="LOGIN_FAILED">Échec connexion</option>
                <option value="LOGOUT">Déconnexion</option>
                <option value="CONFIG_UPDATE">Config système</option>
                <option value="MAINTENANCE_TOGGLE">Maintenance</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ressource</label>
              <select
                value={filters.resourceType}
                onChange={(e) => setFilters({ ...filters, resourceType: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              >
                <option value="">Toutes</option>
                <option value="DOSSIER_MEDICAL">Dossier médical</option>
                <option value="PRESCRIPTION">Prescription</option>
                <option value="RESULTAT_TEST">Résultat test</option>
                <option value="RENDEZ_VOUS">Rendez-vous</option>
                <option value="SUIVI">Suivi patient</option>
                <option value="ADMINISTRATEUR">Administrateur</option>
                <option value="MEDECIN">Médecin</option>
                <option value="SECRETAIRE">Secrétaire</option>
                <option value="PATIENT">Patient</option>
                <option value="SYSTEM_CONFIG">Configuration système</option>
                <option value="SESSION">Session</option>
                <option value="NOTIFICATION">Notification</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recherche</label>
              <input
                type="text"
                placeholder="Email ou patient..."
                value={filters.searchUser}
                onChange={(e) => setFilters({ ...filters, searchUser: e.target.value })}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
              />
            </div>
          </div>
        </div>

        {/* Liste des logs */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-clinic-blue/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Date & Heure</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Utilisateur</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Action</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Ressource</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Patient</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">IP</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-clinic-blue-dark">Détails</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      <i className="ri-file-list-line text-4xl mb-2 block"></i>
                      Aucun log trouvé
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="font-medium text-gray-900">{log.user_name || log.user_email || 'Utilisateur inconnu'}</div>
                        <div className="text-xs text-gray-500">{log.user_role}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action_type)}`}
                        >
                          <i className={`${getActionIcon(log.action_type)} mr-1`}></i>
                          {getActionLabel(log.action_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex items-center">
                          <i className={`${getResourceIcon(log.resource_type)} mr-2 text-gray-400`}></i>
                          {getResourceLabel(log.resource_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{log.patient_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 font-mono">{log.ip_address || '-'}</td>
                      <td className="px-6 py-4 text-xs text-gray-400 max-w-[200px] truncate">
                        {log.details ? (
                          <span title={JSON.stringify(log.details, null, 2)}>
                            {log.details.operation || log.details.patientName || log.details.medecinName || log.details.targetName || '-'}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 bg-clinic-blue/5 border-l-4 border-clinic-blue p-4 rounded-lg">
          <p className="text-sm text-clinic-blue-dark">
            <i className="ri-information-line mr-2"></i>
            <strong>Conformité APDP :</strong> Tous les accès aux données médicales sont enregistrés conformément 
            à la loi n°2017-20 du Bénin sur la protection des données personnelles. Les logs sont conservés pendant 1 an.
          </p>
        </div>
      </div>
    </div>
  );
}